import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DASHBOARD_PREFERENCES_VERSION } from './constants';
import { dashboardPreferencesService } from './service';
import {
  readDashboardPreferencesFromStorage,
  writeDashboardPreferencesToStorage,
} from './storage';
import { DEFAULT_DASHBOARD_PREFERENCES, type DashboardPreferences } from './types';

interface UseDashboardPreferencesOptions {
  userId?: string;
  debounceMs?: number;
  onSyncError?: () => void;
}

const withUpdatedAt = (preferences: DashboardPreferences): DashboardPreferences => ({
  ...preferences,
  hiddenCards: Array.from(new Set(preferences.hiddenCards)),
  version: preferences.version || DASHBOARD_PREFERENCES_VERSION,
  updatedAt: new Date().toISOString(),
});

export const useDashboardPreferences = ({
  userId,
  debounceMs = 500,
  onSyncError,
}: UseDashboardPreferencesOptions) => {
  const [preferences, setPreferences] = useState<DashboardPreferences>(DEFAULT_DASHBOARD_PREFERENCES);
  const [isHydrated, setIsHydrated] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const localMutationRef = useRef(false);
  const syncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingSyncRef = useRef<DashboardPreferences | null>(null);

  const applyPreferences = useCallback(
    (next: DashboardPreferences, markAsMutated: boolean) => {
      const normalized = withUpdatedAt(next);
      setPreferences(normalized);
      if (userId) {
        writeDashboardPreferencesToStorage(userId, normalized);
      }
      if (markAsMutated) {
        localMutationRef.current = true;
      }
      return normalized;
    },
    [userId]
  );

  const syncToBackend = useCallback(
    async (next: DashboardPreferences) => {
      if (!userId) return;
      setIsSyncing(true);
      try {
        const saved = await dashboardPreferencesService.update(next);
        applyPreferences(saved, false);
      } catch {
        onSyncError?.();
      } finally {
        setIsSyncing(false);
      }
    },
    [applyPreferences, onSyncError, userId]
  );

  const scheduleSync = useCallback(
    (next: DashboardPreferences) => {
      pendingSyncRef.current = next;
      if (syncTimerRef.current) {
        clearTimeout(syncTimerRef.current);
      }
      syncTimerRef.current = setTimeout(() => {
        if (pendingSyncRef.current) {
          void syncToBackend(pendingSyncRef.current);
        }
      }, debounceMs);
    },
    [debounceMs, syncToBackend]
  );

  useEffect(() => {
    if (!userId) {
      setPreferences(DEFAULT_DASHBOARD_PREFERENCES);
      setIsHydrated(true);
      return;
    }

    localMutationRef.current = false;
    const cached = readDashboardPreferencesFromStorage(userId);
    setPreferences(cached);
    setIsHydrated(true);

    let cancelled = false;
    const loadFromBackend = async () => {
      try {
        const remote = await dashboardPreferencesService.get();
        if (cancelled || localMutationRef.current) return;
        applyPreferences(remote, false);
      } catch {
        return;
      }
    };

    void loadFromBackend();

    return () => {
      cancelled = true;
    };
  }, [applyPreferences, userId]);

  useEffect(() => {
    return () => {
      if (syncTimerRef.current) {
        clearTimeout(syncTimerRef.current);
      }
    };
  }, []);

  const updateHiddenCards = useCallback(
    (updater: (prev: string[]) => string[]) => {
      const next = applyPreferences(
        {
          ...preferences,
          hiddenCards: updater(preferences.hiddenCards),
        },
        true
      );
      scheduleSync(next);
    },
    [applyPreferences, preferences, scheduleSync]
  );

  const hideCard = useCallback(
    (cardKey: string) => {
      updateHiddenCards((prev) => (prev.includes(cardKey) ? prev : [...prev, cardKey]));
    },
    [updateHiddenCards]
  );

  const showCard = useCallback(
    (cardKey: string) => {
      updateHiddenCards((prev) => prev.filter((key) => key !== cardKey));
    },
    [updateHiddenCards]
  );

  const restoreAllCards = useCallback(() => {
    updateHiddenCards(() => []);
  }, [updateHiddenCards]);

  const setHiddenCards = useCallback(
    (nextHiddenCards: string[]) => {
      const next = applyPreferences(
        {
          ...preferences,
          hiddenCards: nextHiddenCards,
        },
        true
      );
      scheduleSync(next);
    },
    [applyPreferences, preferences, scheduleSync]
  );

  const hiddenCardsSet = useMemo(() => new Set(preferences.hiddenCards), [preferences.hiddenCards]);

  return {
    preferences,
    hiddenCards: preferences.hiddenCards,
    hiddenCardsSet,
    isHydrated,
    isSyncing,
    isCardHidden: (cardKey: string) => hiddenCardsSet.has(cardKey),
    hideCard,
    showCard,
    restoreAllCards,
    setHiddenCards,
  };
};
